import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getApiToken } from '../utils/auth';
import { SectionCard, useToast } from '@vmcom/ui';

interface UnusedBrand {
  id: string;
  name: string;
  lastUsed?: string;
  status: 'used' | 'unused' | 'unknown';
  categories: string[];
  goodsCount: number;
}

interface RobotConfig {
  enabled: boolean;
  checkInterval: number;
  autoMarkUnused: boolean;
  notifyOnUnused: boolean;
  lastCheck?: string;
}

interface CheckLog {
  id: string;
  timestamp: string;
  status: 'running' | 'completed' | 'error';
  totalBrands: number;
  checkedBrands: number;
  unusedBrands: number;
  error?: string;
}

const apiFetch = (url: string, init: Omit<RequestInit, 'headers'> & { headers?: Record<string, string> } = {}) => {
  const token = getApiToken();
  return fetch(url, {
    ...init,
    headers: {
      ...(init.headers || {}),
      ...(token ? { 'X-API-Token': token } : {}),
    },
  });
};

const defaultRobot = (): RobotConfig => ({
  enabled: true,
  checkInterval: 30,
  autoMarkUnused: true,
  notifyOnUnused: true,
});

const UnusedBrandsRobot: React.FC = () => {
  const { toast } = useToast();
  const [robot, setRobot] = useState<RobotConfig>(defaultRobot());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unusedBrands, setUnusedBrands] = useState<UnusedBrand[]>([]);
  const [checkLogs, setCheckLogs] = useState<CheckLog[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [brandsPage, setBrandsPage] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const rowsPerPage = 10;

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      setRobot({
        ...defaultRobot(),
        lastCheck: new Date().toISOString(),
      });

      try {
        const response = await apiFetch('/api/unused-brands');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setUnusedBrands(data.success ? data.data || [] : []);
      } catch (err) {
        console.error('Error loading unused brands:', err);
        setUnusedBrands([]);
      }

      try {
        const response = await apiFetch('/api/unused-brands/logs');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setCheckLogs(data.success ? data.data || [] : []);
      } catch (err) {
        console.error('Error loading logs:', err);
        setCheckLogs([]);
      }
    } catch (err) {
      setError('Ошибка загрузки настроек робота');
      console.error('Error loading unused brands robot settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (robot.checkInterval < 1 || robot.checkInterval > 365) {
      toast('Интервал проверки: от 1 до 365 дней', 'warning');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      console.log('Saving unused brands robot settings:', robot);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast('Настройки робота успешно сохранены!', 'success');
    } catch (err) {
      setError('Ошибка сохранения настроек робота');
      console.error('Error saving unused brands robot settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleStartCheck = async () => {
    try {
      setIsChecking(true);
      setError(null);
      const response = await apiFetch('/api/unused-brands/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.success) {
        toast(
          `Проверка завершена! Найдено ${data.data.unusedBrands} неиспользуемых брендов из ${data.data.totalBrands}`,
          'success',
        );
        await loadSettings();
      } else {
        throw new Error(data.error || 'Ошибка при выполнении проверки');
      }
    } catch (err) {
      setError('Ошибка при запуске проверки');
      console.error('Error starting check:', err);
      toast('Ошибка при выполнении проверки', 'error');
    } finally {
      setIsChecking(false);
    }
  };

  const statusChip = (status: string) => {
    const map: Record<string, { color: 'success' | 'error' | 'warning' | 'info'; label: string; icon: React.ReactElement }> = {
      used: { color: 'success', label: 'Используется', icon: <CheckCircleOutlineIcon /> },
      unused: { color: 'error', label: 'Не используется', icon: <ErrorOutlineIcon /> },
      unknown: { color: 'warning', label: 'Неизвестно', icon: <ScheduleOutlinedIcon /> },
      running: { color: 'info', label: 'Выполняется', icon: <PlayCircleOutlineIcon /> },
      completed: { color: 'success', label: 'Завершена', icon: <CheckCircleOutlineIcon /> },
      error: { color: 'error', label: 'Ошибка', icon: <ErrorOutlineIcon /> },
    };
    const c = map[status] || map.unknown;
    return <Chip size="small" color={c.color} icon={c.icon} label={c.label} />;
  };

  const pagedBrands = unusedBrands.slice(brandsPage * rowsPerPage, brandsPage * rowsPerPage + rowsPerPage);
  const pagedLogs = checkLogs.slice(logsPage * rowsPerPage, logsPage * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Button startIcon={<RefreshOutlinedIcon />} onClick={loadSettings} disabled={loading}>
          Обновить
        </Button>
        <Button variant="contained" startIcon={<PlayCircleOutlineIcon />} onClick={handleStartCheck} disabled={isChecking}>
          {isChecking ? 'Проверка…' : 'Запустить проверку'}
        </Button>
        <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={handleSave} disabled={saving}>
          Сохранить
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !checkLogs.length && !unusedBrands.length ? (
        <CircularProgress size={28} />
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <SectionCard>
                <Typography variant="caption" color="text.secondary">
                  Всего брендов
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <SearchOutlinedIcon color="action" fontSize="small" />
                  <Typography variant="h6">{checkLogs.length > 0 ? checkLogs[0].totalBrands || 0 : 0}</Typography>
                </Stack>
              </SectionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SectionCard>
                <Typography variant="caption" color="text.secondary">
                  Неиспользуемых
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <ErrorOutlineIcon color={unusedBrands.length ? 'error' : 'success'} fontSize="small" />
                  <Typography variant="h6" color={unusedBrands.length ? 'error.main' : 'success.main'}>
                    {unusedBrands.length}
                  </Typography>
                </Stack>
              </SectionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SectionCard>
                <Typography variant="caption" color="text.secondary">
                  Последняя проверка
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <ScheduleOutlinedIcon color="action" fontSize="small" />
                  <Typography variant="body1">
                    {checkLogs.length > 0 && checkLogs[0]
                      ? new Date(checkLogs[0].timestamp).toLocaleDateString('ru-RU')
                      : 'Никогда'}
                  </Typography>
                </Stack>
              </SectionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SectionCard>
                <Typography variant="caption" color="text.secondary">
                  Статус робота
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  <CheckCircleOutlineIcon color={robot.enabled ? 'success' : 'error'} fontSize="small" />
                  <Typography variant="body1" color={robot.enabled ? 'success.main' : 'error.main'}>
                    {robot.enabled ? 'Активен' : 'Отключен'}
                  </Typography>
                </Stack>
              </SectionCard>
            </Grid>
          </Grid>

          <SectionCard title="Настройки робота">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch checked={robot.enabled} onChange={(_, v) => setRobot({ ...robot, enabled: v })} />}
                  label="Включить автоматическую проверку"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Интервал проверки (дни)"
                  inputProps={{ min: 1, max: 365 }}
                  value={robot.checkInterval}
                  onChange={(e) => setRobot({ ...robot, checkInterval: Number(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch checked={robot.autoMarkUnused} onChange={(_, v) => setRobot({ ...robot, autoMarkUnused: v })} />
                  }
                  label="Автоматически помечать неиспользуемые бренды"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch checked={robot.notifyOnUnused} onChange={(_, v) => setRobot({ ...robot, notifyOnUnused: v })} />
                  }
                  label="Уведомлять о неиспользуемых брендах"
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title={`Неиспользуемые бренды (${unusedBrands.length})`}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Бренд</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Товаров</TableCell>
                    <TableCell>Категории</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedBrands.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography fontWeight={600}>{record.name || 'Неизвестный бренд'}</Typography>
                          {record.status === 'unused' && (
                            <Chip size="small" color="error" icon={<ErrorOutlineIcon />} label="Не используется" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{statusChip(record.status)}</TableCell>
                      <TableCell>
                        <Typography color={record.goodsCount === 0 ? 'error' : 'success.main'}>
                          {record.goodsCount ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {(record.categories || []).slice(0, 3).map((cat, index) => (
                            <Chip key={index} size="small" label={cat} />
                          ))}
                          {record.categories && record.categories.length > 3 && (
                            <Tooltip title={record.categories.slice(3).join(', ')}>
                              <Chip size="small" label={`+${record.categories.length - 3}`} />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={unusedBrands.length}
              page={brandsPage}
              onPageChange={(_, p) => setBrandsPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
            />
          </SectionCard>

          <SectionCard title="История проверок">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Время проверки</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Всего брендов</TableCell>
                    <TableCell>Проверено</TableCell>
                    <TableCell>Неиспользуемых</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedLogs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell>
                        {log.timestamp ? new Date(log.timestamp).toLocaleString('ru-RU') : 'Неизвестно'}
                      </TableCell>
                      <TableCell>{statusChip(log.status)}</TableCell>
                      <TableCell>{log.totalBrands}</TableCell>
                      <TableCell>{log.checkedBrands}</TableCell>
                      <TableCell>
                        <Typography color={log.unusedBrands > 0 ? 'error' : 'success.main'}>{log.unusedBrands ?? 0}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={checkLogs.length}
              page={logsPage}
              onPageChange={(_, p) => setLogsPage(p)}
              rowsPerPage={5}
              rowsPerPageOptions={[5]}
            />
          </SectionCard>
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />

      <SectionCard>
        <Typography fontWeight={600} gutterBottom>
          Информация о роботе проверки неиспользуемых брендов
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Робот проверяет бренды на использование в товарах, помечает неиспользуемые и ведёт историю проверок.
        </Typography>
      </SectionCard>
    </Box>
  );
};

export default UnusedBrandsRobot;
